import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './AdminPanel.css';

const AdminPanel = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({});
    const [topics, setTopics] = useState([]);
    const [topicStats, setTopicStats] = useState({});
    const [loading, setLoading] = useState(false);

    const renderShell = (content) => (
        <div className="admin-page">
            <div className="admin-ambient" aria-hidden="true" />
            <div className="admin-panel">
                {content}
            </div>
        </div>
    );

    useEffect(() => {
        if (authLoading) {
            return;
        }

        if (!user) {
            navigate('/');
            return;
        }

        if (!user?.permissions?.can_administrate) {
            alert('您沒有管理員權限，無法訪問此頁面');
            navigate('/');
            return;
        }

        loadDashboardData({ forceTopics: activeTab === 'topics' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (!authLoading && user?.permissions?.can_administrate && activeTab === 'topics') {
            loadDashboardData({ forceTopics: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, authLoading, user]);

    const loadDashboardData = async ({ forceTopics = false } = {}) => {
        setLoading(true);
        try {
            const [usersResponse, statsResponse] = await Promise.all([
                api.get('/users/admin/users/'),
                api.get('/users/admin/dashboard/users/'),
            ]);

            setUsers(usersResponse.data || []);
            setStats(statsResponse.data || {});

            if (forceTopics) {
                const [topicsResponse, topicStatsResponse] = await Promise.all([
                    api.get('/topics/admin/all/'),
                    api.get('/topics/admin/stats/'),
                ]);

                setTopics(topicsResponse.data?.topics || []);
                setTopicStats(topicStatsResponse.data || {});
            }
        } catch (error) {
            console.error('載入管理資料失敗:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleUpdate = async (userId, newRole) => {
        try {
            await api.patch(`/users/admin/users/${userId}/role/`, { role: newRole });
            await loadDashboardData({ forceTopics: activeTab === 'topics' });
            alert('使用者角色已更新');
        } catch (error) {
            console.error('更新角色失敗:', error);
            alert('更新角色失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSuspendUser = async (userId, suspend) => {
        const reason = suspend ? prompt('請輸入停權原因:') : '';
        if (suspend && !reason) return;

        try {
            await api.post(`/users/admin/users/${userId}/suspend/`, {
                is_suspended: suspend,
                suspended_until: suspend ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() : null,
                suspension_reason: reason,
            });
            await loadDashboardData({ forceTopics: activeTab === 'topics' });
            alert(suspend ? '使用者已停權' : '使用者停權已解除');
        } catch (error) {
            console.error('停權操作失敗:', error);
            alert('操作失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleCreateTopic = async () => {
        const name = prompt('請輸入版面名稱:');
        const description = prompt('請輸入版面描述:');
        if (!name) return;

        try {
            await api.post('/topics/admin/create/', {
                name,
                description: description || '',
            });
            await loadDashboardData({ forceTopics: true });
            alert('版面已創建');
        } catch (error) {
            console.error('創建版面失敗:', error);
            alert('創建失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleUpdateTopic = async (topicId) => {
        const currentTopic = (topicStats?.topic_stats || topics || []).find((t) => t.id === topicId) || {};
        const name = prompt('請輸入新的版面名稱:', currentTopic.name);
        const description = prompt('請輸入新的版面描述:', currentTopic.description);
        if (!name) return;

        try {
            await api.put(`/topics/admin/${topicId}/update/`, {
                name,
                description: description || '',
            });
            await loadDashboardData({ forceTopics: true });
            alert('版面已更新');
        } catch (error) {
            console.error('更新版面失敗:', error);
            alert('更新失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteTopic = async (topicId) => {
        const topic = (topicStats?.topic_stats || topics || []).find((t) => t.id === topicId);
        if (!topic) return;
        if (!window.confirm(`確定要刪除版面 "${topic.name}" 嗎？`)) return;

        try {
            await api.delete(`/topics/admin/${topicId}/delete/`);
            await loadDashboardData({ forceTopics: true });
            alert('版面已刪除');
        } catch (error) {
            console.error('刪除版面失敗:', error);
            alert('刪除失敗: ' + (error.response?.data?.error || error.message));
        }
    };

    if (authLoading) {
        return renderShell(
            <>
                <header className="admin-header">
                    <div className="admin-header-glow" aria-hidden="true" />
                    <h1 className="admin-title">管理員控制台</h1>
                    <p className="admin-subtitle">正在準備系統管理資料...</p>
                </header>
                <div className="admin-loading">載入中...</div>
            </>
        );
    }

    if (!user || !user?.permissions?.can_administrate) {
        return null;
    }

    if (loading) {
        return renderShell(
            <>
                <header className="admin-header">
                    <div className="admin-header-glow" aria-hidden="true" />
                    <h1 className="admin-title">管理員控制台</h1>
                    <p className="admin-subtitle">歡迎，{user?.username} 管理員</p>
                </header>
                <div className="admin-loading">載入中...</div>
            </>
        );
    }

    const moderatorCount = users.filter((u) => u.role === 'moderator').length;
    const adminCount = users.filter((u) => u.role === 'admin').length;
    const userCount = users.filter((u) => u.role === 'user').length;
    const topicRows = (topicStats?.topic_stats && topicStats.topic_stats.length)
        ? topicStats.topic_stats
        : (topics || []).map((topic) => ({
            id: topic.id,
            name: topic.name,
            description: topic.description,
            post_count: topic.post_count || topic.posts_count || 0,
            created_at: topic.created_at || topic.createdAt || topic.created || new Date().toISOString(),
        }));

    return renderShell(
        <>
            <header className="admin-header">
                <div className="admin-header-glow" aria-hidden="true" />
                <h1 className="admin-title">🔧 系統管理面板</h1>
                <p className="admin-subtitle">歡迎，{user?.username} 管理員</p>
                <div className="admin-meta">
                    <span className="admin-pill">🛡️ 角色：{user?.permissions?.role_label || '管理員'}</span>
                    <span className="admin-pill">👥 總使用者：{stats.total_users || users.length || 0}</span>
                    <span className="admin-pill">⏱️ 最近同步：{stats.last_sync ? new Date(stats.last_sync).toLocaleString('zh-TW') : '尚未同步'}</span>
                </div>
            </header>

            <div className="admin-stats">
                <div className="stat-card">
                    <h3>總使用者數</h3>
                    <div className="stat-number">{stats.total_users || users.length || 0}</div>
                </div>
                <div className="stat-card">
                    <h3>管理員</h3>
                    <div className="stat-number">{adminCount}</div>
                </div>
                <div className="stat-card">
                    <h3>版主</h3>
                    <div className="stat-number">{moderatorCount}</div>
                </div>
                <div className="stat-card">
                    <h3>一般使用者</h3>
                    <div className="stat-number">{userCount}</div>
                </div>
            </div>

            <div className="admin-tabs">
                <button className={activeTab === 'users' ? 'active' : ''} onClick={() => setActiveTab('users')}>
                    👥 使用者管理
                </button>
                <button className={activeTab === 'topics' ? 'active' : ''} onClick={() => setActiveTab('topics')}>
                    📝 版面管理
                </button>
                <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>
                    ⚙️ 系統設定
                </button>
            </div>

            {activeTab === 'users' && (
                <section className="admin-content">
                    <div className="admin-section-header">
                        <div>
                            <h2>使用者管理</h2>
                            <p className="admin-section-subtitle">調整權限、停權或檢視帳號狀態，維護社群秩序</p>
                        </div>
                        <button className="admin-refresh" onClick={() => loadDashboardData({ forceTopics: false })}>
                            🔄 重新整理
                        </button>
                    </div>
                    <div className="users-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>使用者名稱</th>
                                    <th>電子郵件</th>
                                    <th>角色</th>
                                    <th>狀態</th>
                                    <th>註冊時間</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((userItem) => (
                                    <tr key={userItem.id} className={userItem.is_suspended ? 'is-suspended' : ''}>
                                        <td>
                                            <span className="cell-primary">{userItem.username}</span>
                                            {userItem.id === user.id && <span className="current-user">（當前使用者）</span>}
                                        </td>
                                        <td>{userItem.email || '—'}</td>
                                        <td>
                                            <span className={`role-badge role-${userItem.role}`}>
                                                {userItem.role_display}
                                            </span>
                                        </td>
                                        <td>
                                            {userItem.is_suspended ? (
                                                <span className="status status-suspended">已停權</span>
                                            ) : (
                                                <span className="status status-active">正常</span>
                                            )}
                                        </td>
                                        <td>{new Date(userItem.date_joined).toLocaleDateString('zh-TW')}</td>
                                        <td className="user-actions">
                                            {userItem.id !== user.id && (
                                                <>
                                                    <select
                                                        value={userItem.role}
                                                        onChange={(e) => handleRoleUpdate(userItem.id, e.target.value)}
                                                        className="role-select"
                                                    >
                                                        <option value="user">一般使用者</option>
                                                        <option value="moderator">版主</option>
                                                        <option value="admin">管理員</option>
                                                    </select>
                                                    <button
                                                        onClick={() => handleSuspendUser(userItem.id, !userItem.is_suspended)}
                                                        className={userItem.is_suspended ? 'admin-success' : 'admin-danger'}
                                                    >
                                                        {userItem.is_suspended ? '解除停權' : '停權'}
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {activeTab === 'topics' && (
                <section className="admin-content">
                    <div className="admin-section-header">
                        <div>
                            <h2>版面管理</h2>
                            <p className="admin-section-subtitle">建立或調整版面資訊，並協調版主資源</p>
                        </div>
                        <button className="admin-primary" onClick={handleCreateTopic}>
                            ➕ 新增版面
                        </button>
                    </div>
                    <div className="admin-highlight">
                        <span>總版面：{topicStats.total_topics || topics.length || 0}</span>
                        <span>文章總數：{topicStats.total_posts || 0}</span>
                        <span>留言總數：{topicStats.total_comments || 0}</span>
                    </div>
                    <div className="topics-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>版面名稱</th>
                                    <th>描述</th>
                                    <th>文章數</th>
                                    <th>建立日期</th>
                                    <th>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topicRows.map((topic) => (
                                    <tr key={topic.id}>
                                        <td className="cell-primary">{topic.name}</td>
                                        <td>{topic.description || '無描述'}</td>
                                        <td>{topic.post_count}</td>
                                        <td>{new Date(topic.created_at).toLocaleDateString('zh-TW')}</td>
                                        <td className="topic-actions">
                                            <button className="admin-secondary" onClick={() => handleUpdateTopic(topic.id)}>
                                                ✏️ 編輯
                                            </button>
                                            <button className="admin-danger" onClick={() => handleDeleteTopic(topic.id)}>
                                                🗑️ 刪除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {topicRows.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="empty-state">目前尚無版面資料</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {activeTab === 'settings' && (
                <section className="admin-content">
                    <div className="admin-section-header">
                        <div>
                            <h2>系統設定</h2>
                            <p className="admin-section-subtitle">快速進行維護作業，保持系統穩定運作</p>
                        </div>
                    </div>
                    <div className="settings-grid">
                        <div className="setting-item">
                            <h3>同步統計資料</h3>
                            <p>重新計算使用者、文章與互動數據，確保儀表板顯示即時資訊。</p>
                            <button className="admin-primary" onClick={() => loadDashboardData({ forceTopics: activeTab === 'topics' })}>
                                立即同步
                            </button>
                        </div>
                        <div className="setting-item">
                            <h3>維護模式</h3>
                            <p>暫時關閉使用者操作以進行系統升級或例行維護工作。</p>
                            <button className="admin-secondary" onClick={() => alert('維護模式功能開發中...')}>
                                啟用維護模式
                            </button>
                        </div>
                        <div className="setting-item">
                            <h3>資料備份</h3>
                            <p>快速下載最新資料備份以確保資料安全，建議每週至少執行一次。</p>
                            <button className="admin-secondary" onClick={() => alert('資料備份功能開發中...')}>
                                下載備份
                            </button>
                        </div>
                        <div className="setting-item">
                            <h3>檢視系統紀錄</h3>
                            <p>快速檢視錯誤與操作紀錄，協助診斷系統狀態。</p>
                            <button className="admin-secondary" onClick={() => alert('系統紀錄功能開發中...')}>
                                查看紀錄
                            </button>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default AdminPanel;
